import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { clearAuthStorage, getRefreshToken, getToken, setRefreshToken, setToken } from "./token";
import { refreshAccessToken } from "./sessionRefresh";
import { isTokenExpired, msUntilTokenExpiry } from "./jwtUtils";

const API_BASE = process.env.REACT_APP_API_URL || "";

const AuthContext = createContext(null);

function authRequestConfig(token) {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    return { headers, withCredentials: true };
}

function isTransientError(error) {
    return !error?.response;
}

async function fetchCurrentUser(token) {
    const response = await axios.get(`${API_BASE}/api/me`, authRequestConfig(token));
    return response.data || null;
}

async function ensureValidAccessToken() {
    const refreshToken = getRefreshToken();
    const accessToken = getToken();

    if (!accessToken && !refreshToken) {
        return null;
    }

    if ((!accessToken || isTokenExpired(accessToken)) && refreshToken) {
        await refreshAccessToken();
    }

    return getToken() || null;
}

async function restoreUserFromStorage() {
    const token = await ensureValidAccessToken();
    if (!token) {
        return null;
    }

    try {
        return await fetchCurrentUser(token);
    } catch (error) {
        if (error?.response?.status !== 401 || !getRefreshToken()) {
            throw error;
        }
    }

    await refreshAccessToken();
    const refreshedToken = getToken();
    if (!refreshedToken) {
        return null;
    }

    return fetchCurrentUser(refreshedToken);
}

async function restoreUserWithRetry(maxAttempts = 4) {
    let lastError = null;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        try {
            return await restoreUserFromStorage();
        } catch (error) {
            lastError = error;
            if (!isTransientError(error) || attempt === maxAttempts - 1) {
                throw error;
            }
            await new Promise((resolve) => {
                setTimeout(resolve, 750 * (attempt + 1));
            });
        }
    }

    throw lastError;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [ready, setReady] = useState(false);

    const refresh = useCallback(() => {
        restoreUserWithRetry()
            .then((userData) => setUser(userData))
            .catch((error) => {
                if (isTransientError(error)) {
                    return;
                }
                if (error?.response?.status === 401) {
                    clearAuthStorage();
                }
                setUser(null);
            })
            .finally(() => setReady(true));
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        let refreshTimerId = null;

        const scheduleRefresh = () => {
            if (refreshTimerId) {
                clearTimeout(refreshTimerId);
                refreshTimerId = null;
            }

            const token = getToken();
            const refreshToken = getRefreshToken();
            if (!token || !refreshToken) {
                return;
            }

            const delay = msUntilTokenExpiry(token, 60_000);
            if (delay === null || delay <= 0) {
                refreshAccessToken()
                    .then(() => scheduleRefresh())
                    .catch(() => {});
                return;
            }

            refreshTimerId = setTimeout(() => {
                refreshAccessToken()
                    .then(() => scheduleRefresh())
                    .catch(() => {});
            }, delay);
        };

        scheduleRefresh();

        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                const token = getToken();
                if (token && isTokenExpired(token) && getRefreshToken()) {
                    refreshAccessToken()
                        .then(() => scheduleRefresh())
                        .catch(() => {});
                } else {
                    scheduleRefresh();
                }
            }
        };

        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            if (refreshTimerId) {
                clearTimeout(refreshTimerId);
            }
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [user]);

    useEffect(() => {
        const onForcedLogout = () => {
            clearAuthStorage();
            setUser(null);
            setReady(true);
            if (!window.location.pathname.includes("/login")) {
                window.location.assign(`${window.location.pathname.startsWith("/en") ? "/en" : "/ru"}/login`);
            }
        };
        window.addEventListener("auth:logout-required", onForcedLogout);
        return () => window.removeEventListener("auth:logout-required", onForcedLogout);
    }, []);

    const logout = useCallback(() => {
        const token = getToken();
        const refreshToken = getRefreshToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        clearAuthStorage();
        setUser(null);
        axios
            .post(
                `${API_BASE}/api/logout`,
                refreshToken ? { refresh_token: refreshToken } : {},
                { headers, withCredentials: true, skipAuth: true },
            )
            .catch(() => {});
    }, []);

    const loginWithToken = useCallback(async (token, refreshToken = "") => {
        setToken(token);
        if (refreshToken) {
            setRefreshToken(refreshToken);
        }
        try {
            const response = await axios.get(`${API_BASE}/api/me`, authRequestConfig(token));
            setUser(response.data || null);
        } catch {
            setUser(null);
            clearAuthStorage();
        } finally {
            setReady(true);
        }
    }, []);

    const value = useMemo(
        () => ({
            user,
            ready,
            isAuthenticated: !!user,
            refresh,
            logout,
            loginWithToken,
        }),
        [user, ready, refresh, logout, loginWithToken],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used within AuthProvider");
    }
    return ctx;
}
